import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Building2,
  Shield,
  FileText,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Calendar,
  Users,
  DollarSign,
  Wrench,
  Bell,
  ArrowLeft,
  Download,
  Plus,
  RefreshCw,
  Loader2,
  MapPin,
  Home,
  Briefcase,
  TrendingUp,
  AlertCircle,
  ChevronRight,
  File,
  Upload,
  ExternalLink,
  History,
  Edit2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Compliance type icons and colors
const COMPLIANCE_CONFIG = {
  insurance: { icon: Shield, color: 'indigo', label: 'Insurance' },
  fire_safety: { icon: AlertTriangle, color: 'red', label: 'Fire Safety' },
  electrical: { icon: TrendingUp, color: 'amber', label: 'Electrical' },
  building_inspection: { icon: Building2, color: 'blue', label: 'Building Inspection' },
  levy_clearance: { icon: DollarSign, color: 'emerald', label: 'Levy Clearance' },
  occupancy: { icon: Home, color: 'purple', label: 'Occupancy' },
};

const STATUS_COLORS = {
  valid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  expiring_soon: 'bg-amber-100 text-amber-700 border-amber-200',
  attention: 'bg-orange-100 text-orange-700 border-orange-200',
  expired: 'bg-red-100 text-red-700 border-red-200',
  missing: 'bg-slate-100 text-slate-700 border-slate-200',
  unknown: 'bg-slate-100 text-slate-500 border-slate-200',
};

export default function DigitalBuildingRecord() {
  const { hoaId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [buildingData, setBuildingData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [transfers, setTransfers] = useState([]);
  
  // Dialog states
  const [showComplianceDialog, setShowComplianceDialog] = useState(false);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  
  // Form states
  const [complianceForm, setComplianceForm] = useState({
    compliance_type: '',
    certificate_number: '',
    issued_date: '',
    expiry_date: '',
    issuing_authority: '',
    notes: '',
    document_url: ''
  });
  
  const [documentForm, setDocumentForm] = useState({
    document_type: '',
    title: '',
    description: '',
    document_url: '',
    expiry_date: ''
  });

  useEffect(() => {
    if (hoaId) {
      fetchBuildingRecord();
    }
  }, [hoaId]);

  const fetchBuildingRecord = async () => {
    setLoading(true);
    try {
      const [recordRes, timelineRes, transfersRes] = await Promise.all([
        fetch(`${API_URL}/api/building-records/${hoaId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/building-records/${hoaId}/timeline?limit=50`, { credentials: 'include' }),
        fetch(`${API_URL}/api/building-records/${hoaId}/transfers`, { credentials: 'include' })
      ]);
      
      if (recordRes.ok) {
        setBuildingData(await recordRes.json());
      } else {
        toast.error('Failed to load building record');
      }
      
      if (timelineRes.ok) {
        const data = await timelineRes.json();
        setTimeline(data.activities || []);
      }
      
      if (transfersRes.ok) {
        const data = await transfersRes.json();
        setTransfers(data.transfers || []);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load building data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompliance = async () => {
    if (!complianceForm.compliance_type || !complianceForm.expiry_date) {
      toast.error('Please fill required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/building-records/${hoaId}/compliance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(complianceForm)
      });
      
      if (response.ok) {
        toast.success('Compliance record saved');
        setShowComplianceDialog(false);
        setComplianceForm({
          compliance_type: '', certificate_number: '', issued_date: '',
          expiry_date: '', issuing_authority: '', notes: '', document_url: ''
        });
        fetchBuildingRecord();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save compliance record');
      }
    } catch (error) {
      toast.error('Failed to save compliance record');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddDocument = async () => {
    if (!documentForm.title || !documentForm.document_url) {
      toast.error('Please fill required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/building-records/${hoaId}/documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(documentForm)
      });
      
      if (response.ok) {
        toast.success('Document added');
        setShowDocumentDialog(false);
        setDocumentForm({ document_type: '', title: '', description: '', document_url: '', expiry_date: '' });
        fetchBuildingRecord();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add document');
      }
    } catch (error) {
      toast.error('Failed to add document');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await fetch(`${API_URL}/api/building-records/${hoaId}/export/pdf`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `building_record_${hoaId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('PDF downloaded');
      } else {
        toast.error('Failed to generate PDF');
      }
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `R${(amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  };

  const getStatusBadge = (status) => {
    const labels = {
      valid: 'Valid',
      expiring_soon: 'Expiring Soon',
      attention: 'Needs Attention',
      expired: 'Expired',
      missing: 'Missing',
      unknown: 'Unknown'
    };
    return (
      <Badge className={`${STATUS_COLORS[status] || STATUS_COLORS.unknown} border`}>
        {status === 'valid' && <CheckCircle2 className="w-3 h-3 mr-1" />}
        {status === 'expiring_soon' && <Clock className="w-3 h-3 mr-1" />}
        {status === 'expired' && <XCircle className="w-3 h-3 mr-1" />}
        {status === 'missing' && <AlertCircle className="w-3 h-3 mr-1" />}
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!buildingData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Building not found or access denied</p>
          <Button className="mt-4" onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  const { building, compliance, units, financial, management, activity } = buildingData;
  const complianceScore = compliance?.summary ? 
    Math.round((compliance.summary.valid / compliance.summary.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="digital-building-record">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Building2 className="w-6 h-6 text-indigo-600" />
                  {building?.name || 'Building Record'}
                </h1>
                <p className="text-sm text-slate-500 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {building?.address || 'No address'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={fetchBuildingRecord}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={handleExportPdf} disabled={exportingPdf}>
                {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Export PDF
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border mb-6">
            <TabsTrigger value="overview" className="gap-2">
              <Building2 className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="compliance" className="gap-2">
              <Shield className="w-4 h-4" />
              Compliance
              {compliance?.summary?.expired > 0 && (
                <span className="ml-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {compliance.summary.expired}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="documents" className="gap-2">
              <FileText className="w-4 h-4" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="activity" className="gap-2">
              <Clock className="w-4 h-4" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <History className="w-4 h-4" />
              History
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Left Column - Building Info */}
              <div className="lg:col-span-2 space-y-6">
                {/* Quick Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <Home className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{units?.total_units || 0}</p>
                          <p className="text-xs text-slate-500">Total Units</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <Users className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{units?.total_residents || 0}</p>
                          <p className="text-xs text-slate-500">Residents</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <Wrench className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{activity?.tickets || 0}</p>
                          <p className="text-xs text-slate-500">Tickets</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{activity?.documents || 0}</p>
                          <p className="text-xs text-slate-500">Documents</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Building Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Building Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">Address</p>
                        <p className="font-medium">{building?.address || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Country</p>
                        <p className="font-medium">{building?.country || 'South Africa'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Building Type</p>
                        <p className="font-medium capitalize">{building?.building_type || 'Residential'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total Units</p>
                        <p className="font-medium">{building?.total_units || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Status</p>
                        <Badge className={building?.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                          {building?.status?.toUpperCase() || 'N/A'}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Registration #</p>
                        <p className="font-medium">{building?.registration_number || 'N/A'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Management Info */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-indigo-600" />
                      Management
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">Ownership Type</p>
                        <p className="font-medium capitalize">{management?.ownership_type || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Managing Agent</p>
                        <p className="font-medium">{management?.managing_agent_company || 'Self-managed'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Assigned Agent</p>
                        <p className="font-medium">{management?.assigned_agent || 'N/A'}</p>
                      </div>
                      {management?.transfer_date && (
                        <div>
                          <p className="text-sm text-slate-500">Transfer Date</p>
                          <p className="font-medium">{formatDate(management.transfer_date)}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Compliance & Financial Summary */}
              <div className="space-y-6">
                {/* Compliance Score */}
                <Card className={compliance?.summary?.expired > 0 ? 'border-red-200 bg-red-50/30' : ''}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-indigo-600" />
                        Compliance Score
                      </span>
                      <Button size="sm" variant="outline" onClick={() => setActiveTab('compliance')}>
                        View All
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center mb-4">
                      <div className="text-4xl font-bold text-indigo-600">{complianceScore}%</div>
                      <p className="text-sm text-slate-500">Compliance Rate</p>
                    </div>
                    <Progress value={complianceScore} className="h-2 mb-4" />
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-500" />
                        <span>Valid: {compliance?.summary?.valid || 0}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-amber-500" />
                        <span>Expiring: {compliance?.summary?.expiring_soon || 0}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <span>Expired: {compliance?.summary?.expired || 0}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-slate-400" />
                        <span>Missing: {compliance?.summary?.missing || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Financial Summary */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-emerald-600" />
                      Financial Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-500">Subscription</span>
                        <Badge className={financial?.subscription_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                          {financial?.subscription_status?.toUpperCase() || 'N/A'}
                        </Badge>
                      </div>
                      {financial?.subscription_end && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-500">Expires</span>
                          <span className="font-medium">{formatDate(financial.subscription_end)}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-500">Total Invoices</span>
                        <span className="font-medium">{financial?.total_invoices || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-500">Paid</span>
                        <span className="font-medium text-emerald-600">{financial?.paid_invoices || 0}</span>
                      </div>
                      {financial?.outstanding_amount > 0 && (
                        <div className="flex justify-between items-center p-2 bg-amber-50 rounded">
                          <span className="text-sm text-amber-700">Outstanding</span>
                          <span className="font-bold text-amber-700">{formatCurrency(financial.outstanding_amount)}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Button className="w-full justify-start" variant="outline" onClick={() => {
                      setActiveTab('compliance');
                      setShowComplianceDialog(true);
                    }}>
                      <Shield className="w-4 h-4 mr-2" />
                      Add Compliance Record
                    </Button>
                    <Button className="w-full justify-start" variant="outline" onClick={() => {
                      setActiveTab('documents');
                      setShowDocumentDialog(true);
                    }}>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Document
                    </Button>
                    <Button className="w-full justify-start" variant="outline" onClick={handleExportPdf} disabled={exportingPdf}>
                      {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      Export Full Report
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">Compliance Records</h2>
                  <p className="text-sm text-slate-500">Track insurance, certificates, and inspections</p>
                </div>
                <Button onClick={() => setShowComplianceDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Record
                </Button>
              </div>

              {/* Alerts Banner */}
              {(compliance?.summary?.expired > 0 || compliance?.summary?.expiring_soon > 0) && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Compliance Alerts</p>
                      <p className="text-sm text-amber-700">
                        {compliance?.summary?.expired > 0 && `${compliance.summary.expired} expired. `}
                        {compliance?.summary?.expiring_soon > 0 && `${compliance.summary.expiring_soon} expiring soon.`}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Compliance Grid */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {compliance?.records?.map((record, idx) => {
                  const config = COMPLIANCE_CONFIG[record.compliance_type] || { icon: Shield, color: 'slate', label: record.name };
                  const Icon = config.icon;
                  
                  return (
                    <Card key={idx} className={`hover:shadow-md transition-shadow ${record.status_info?.status === 'expired' ? 'border-red-200' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className={`w-10 h-10 bg-${config.color}-100 rounded-lg flex items-center justify-center`}>
                            <Icon className={`w-5 h-5 text-${config.color}-600`} />
                          </div>
                          {getStatusBadge(record.status_info?.status || 'unknown')}
                        </div>
                        <h3 className="font-medium text-slate-900">{record.name || config.label}</h3>
                        {record.certificate_number && (
                          <p className="text-sm text-slate-500">#{record.certificate_number}</p>
                        )}
                        {record.expiry_date && (
                          <p className="text-sm mt-2">
                            <span className="text-slate-500">Expires: </span>
                            <span className={record.status_info?.status === 'expired' ? 'text-red-600 font-medium' : ''}>
                              {formatDate(record.expiry_date)}
                            </span>
                          </p>
                        )}
                        {record.status_info?.days_until_expiry !== null && record.status_info?.days_until_expiry !== undefined && (
                          <p className="text-xs text-slate-400 mt-1">
                            {record.status_info.days_until_expiry < 0 
                              ? `Expired ${Math.abs(record.status_info.days_until_expiry)} days ago`
                              : `${record.status_info.days_until_expiry} days remaining`}
                          </p>
                        )}
                        {record.is_missing && (
                          <p className="text-sm text-amber-600 mt-2">No record on file</p>
                        )}
                        {record.document_url && (
                          <Button variant="link" className="p-0 h-auto mt-2 text-xs" asChild>
                            <a href={record.document_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-3 h-3 mr-1" />
                              View Document
                            </a>
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">Building Documents</h2>
                  <p className="text-sm text-slate-500">Title deeds, rules, certificates, and more</p>
                </div>
                <Button onClick={() => setShowDocumentDialog(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Document
                </Button>
              </div>

              {activity?.documents === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No documents uploaded yet</p>
                    <Button className="mt-4" onClick={() => setShowDocumentDialog(true)}>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload First Document
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Document</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Uploaded</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                            Documents will appear here
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Activity</CardTitle>
                <CardDescription>Timeline of building activities</CardDescription>
              </CardHeader>
              <CardContent>
                {timeline.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p>No recent activity</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {timeline.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 hover:bg-slate-50 rounded-lg">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          item.type === 'ticket' ? 'bg-amber-100' :
                          item.type === 'notice' ? 'bg-blue-100' :
                          item.type === 'booking' ? 'bg-purple-100' :
                          'bg-slate-100'
                        }`}>
                          {item.type === 'ticket' && <Wrench className="w-4 h-4 text-amber-600" />}
                          {item.type === 'notice' && <Bell className="w-4 h-4 text-blue-600" />}
                          {item.type === 'booking' && <Calendar className="w-4 h-4 text-purple-600" />}
                          {item.type === 'document' && <FileText className="w-4 h-4 text-slate-600" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{item.title}</p>
                          <p className="text-xs text-slate-500">{formatDate(item.timestamp)}</p>
                        </div>
                        {item.status && (
                          <Badge variant="outline" className="text-xs">
                            {item.status}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Management History</CardTitle>
                <CardDescription>Ownership and transfer records</CardDescription>
              </CardHeader>
              <CardContent>
                {transfers.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p>No transfer history</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {transfers.map((transfer, idx) => (
                      <div key={idx} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">
                              Transferred to {transfer.target_company_name || 'Managing Agent'}
                            </p>
                            <p className="text-sm text-slate-500">
                              From: {transfer.requesting_user_name}
                            </p>
                          </div>
                          <Badge className={
                            transfer.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                            transfer.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }>
                            {transfer.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">
                          {formatDate(transfer.created_at)}
                          {transfer.resolved_at && ` - Resolved ${formatDate(transfer.resolved_at)}`}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Compliance Dialog */}
      <Dialog open={showComplianceDialog} onOpenChange={setShowComplianceDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-600" />
              Add Compliance Record
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Compliance Type *</Label>
              <Select 
                value={complianceForm.compliance_type} 
                onValueChange={(v) => setComplianceForm({...complianceForm, compliance_type: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(compliance?.types || COMPLIANCE_CONFIG).map(([key, info]) => (
                    <SelectItem key={key} value={key}>{info.name || info.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Certificate Number</Label>
                <Input
                  value={complianceForm.certificate_number}
                  onChange={(e) => setComplianceForm({...complianceForm, certificate_number: e.target.value})}
                  placeholder="e.g., INS-2024-001"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Issuing Authority</Label>
                <Input
                  value={complianceForm.issuing_authority}
                  onChange={(e) => setComplianceForm({...complianceForm, issuing_authority: e.target.value})}
                  placeholder="e.g., SAIA"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Issue Date</Label>
                <Input
                  type="date"
                  value={complianceForm.issued_date}
                  onChange={(e) => setComplianceForm({...complianceForm, issued_date: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Expiry Date *</Label>
                <Input
                  type="date"
                  value={complianceForm.expiry_date}
                  onChange={(e) => setComplianceForm({...complianceForm, expiry_date: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Document URL</Label>
              <Input
                value={complianceForm.document_url}
                onChange={(e) => setComplianceForm({...complianceForm, document_url: e.target.value})}
                placeholder="https://..."
                className="mt-1"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={complianceForm.notes}
                onChange={(e) => setComplianceForm({...complianceForm, notes: e.target.value})}
                placeholder="Additional notes..."
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComplianceDialog(false)}>Cancel</Button>
            <Button onClick={handleAddCompliance} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Record'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Document Dialog */}
      <Dialog open={showDocumentDialog} onOpenChange={setShowDocumentDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-indigo-600" />
              Upload Document
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Document Type</Label>
              <Select 
                value={documentForm.document_type} 
                onValueChange={(v) => setDocumentForm({...documentForm, document_type: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="title_deed">Title Deed</SelectItem>
                  <SelectItem value="scheme_rules">Scheme Rules</SelectItem>
                  <SelectItem value="insurance_cert">Insurance Certificate</SelectItem>
                  <SelectItem value="fire_cert">Fire Certificate</SelectItem>
                  <SelectItem value="electrical_cert">Electrical Certificate</SelectItem>
                  <SelectItem value="financial_statement">Financial Statement</SelectItem>
                  <SelectItem value="budget">Budget</SelectItem>
                  <SelectItem value="agm_minutes">AGM Minutes</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input
                value={documentForm.title}
                onChange={(e) => setDocumentForm({...documentForm, title: e.target.value})}
                placeholder="Document title"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Document URL *</Label>
              <Input
                value={documentForm.document_url}
                onChange={(e) => setDocumentForm({...documentForm, document_url: e.target.value})}
                placeholder="https://..."
                className="mt-1"
              />
            </div>
            <div>
              <Label>Expiry Date (if applicable)</Label>
              <Input
                type="date"
                value={documentForm.expiry_date}
                onChange={(e) => setDocumentForm({...documentForm, expiry_date: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={documentForm.description}
                onChange={(e) => setDocumentForm({...documentForm, description: e.target.value})}
                placeholder="Brief description..."
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocumentDialog(false)}>Cancel</Button>
            <Button onClick={handleAddDocument} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upload Document'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
